/**
 * Global app state (zustand v5). Keep it small: only state that several owners share.
 *
 * - In React: `const phase = useAppStore((s) => s.phase)` (select one field at a time).
 * - In `useFrame` / GSAP callbacks: `useAppStore.getState().phase` (no re-render).
 * - To react without rendering: `useAppStore.subscribe((state, prev) => { ... })`.
 *
 * @module
 */
import { create } from 'zustand'
import { forceReducedMotion, IS_DEV, isDebug, playgroundSection, qualityOverride } from './flags'
import { detectQualityTier, type QualityTier } from './quality'

/**
 * Page lifecycle.
 * - `boot`: the preloader covers the page and scroll is locked.
 * - `reveal`: the preloader is leaving; the hero and chrome play their intros now.
 * - `ready`: the intro is done and scroll is unlocked.
 *
 * Phases only move forward, but a component can mount in any of them (playground mode
 * starts at `ready`). If it is already past `boot`, play the intro or jump to its end state.
 */
export type AppPhase = 'boot' | 'reveal' | 'ready'

/** Shape of {@link useAppStore}. */
export interface AppState {
  /** Current lifecycle phase; see {@link AppPhase}. */
  phase: AppPhase
  /** Moves the lifecycle on. The Preloader owns this call. */
  setPhase: (phase: AppPhase) => void

  /** Scene ids (from `<SceneCanvas id>`) that have rendered at least one frame. */
  readyScenes: Record<string, true>
  /** Marks a scene as ready. `SceneReady` calls this after the first rendered frame. */
  markSceneReady: (id: string) => void

  /** Current WebGL quality tier. Read presets with `useQuality()`. */
  quality: QualityTier
  /**
   * Steps quality down one tier (high → medium → low). It never steps back up during a
   * session, and it does nothing when `?quality=` pins the tier.
   */
  degradeQuality: () => void

  /** `prefers-reduced-motion: reduce` or `?motion=reduced`. Kept in sync automatically. */
  reducedMotion: boolean

  /** Whether the full-screen nav menu is open. */
  menuOpen: boolean
  /** Opens or closes the nav menu. */
  setMenuOpen: (open: boolean) => void
}

const LOWER_TIER: Record<QualityTier, QualityTier> = { high: 'medium', medium: 'low', low: 'low' }

const reducedMotionQuery =
  typeof window === 'undefined' ? null : window.matchMedia('(prefers-reduced-motion: reduce)')

/** The single app store. See the module docs for usage patterns. */
export const useAppStore = create<AppState>()((set, get) => ({
  phase: playgroundSection !== null ? 'ready' : 'boot',
  setPhase: (phase) => set({ phase }),

  readyScenes: {},
  markSceneReady: (id) => {
    if (get().readyScenes[id]) return
    set((state) => ({ readyScenes: { ...state.readyScenes, [id]: true } }))
  },

  quality: detectQualityTier(),
  degradeQuality: () => {
    if (qualityOverride) return
    const current = get().quality
    const next = LOWER_TIER[current]
    if (next === current) return
    set({ quality: next })
    if (IS_DEV || isDebug) console.info(`[vokarta] quality degraded: ${current} → ${next}`)
  },

  reducedMotion: forceReducedMotion || (reducedMotionQuery?.matches ?? false),

  menuOpen: false,
  setMenuOpen: (menuOpen) => set({ menuOpen }),
}))

/**
 * Keeps `reducedMotion` in sync with the OS setting and mirrors it onto
 * `<html data-motion="reduced|full">`, so CSS can honour `?motion=reduced` too.
 */
function syncReducedMotion(): void {
  const reducedMotion = forceReducedMotion || (reducedMotionQuery?.matches ?? false)
  useAppStore.setState({ reducedMotion })
  document.documentElement.dataset.motion = reducedMotion ? 'reduced' : 'full'
}

if (reducedMotionQuery) {
  syncReducedMotion()
  reducedMotionQuery.addEventListener('change', syncReducedMotion)
  import.meta.hot?.dispose(() =>
    reducedMotionQuery.removeEventListener('change', syncReducedMotion),
  )
}
